import { Injectable } from '@angular/core';

export interface TrainerFilterState {
  golfCourseSearch?: string;
  selectedGolfCourse?: {
    id: number;
    name: string;
    golf_course_name: string;
    address?: string;
    street_address?: string;
    city?: string;
    state?: string;
    zip?: string;
  } | null;
  areaSearch?: string;
  selectedArea?: string;
  selectedAreaData?: any;
  dateRangeStart?: string;
  dateRangeEnd?: string;
  selectedDate?: string;
  startDateModel?: string | null; // ISO string for storage
  endDateModel?: string | null; // ISO string for storage
  hasSearched?: boolean;
}

// Input type for saveFilterState - accepts Date objects from component
export interface TrainerFilterStateInput {
  golfCourseSearch?: string;
  selectedGolfCourse?: {
    id: number;
    name: string;
    golf_course_name: string;
    address?: string;
    street_address?: string;
    city?: string;
    state?: string;
    zip?: string;
  } | null;
  areaSearch?: string;
  selectedArea?: string;
  selectedAreaData?: any;
  dateRangeStart?: string;
  dateRangeEnd?: string;
  selectedDate?: string;
  startDateModel?: Date | string | null; // Can be Date or string
  endDateModel?: Date | string | null; // Can be Date or string
  hasSearched?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TrainerFilterStateService {
  private readonly STORAGE_KEY = 'trainer_filter_state';

  /**
   * Save current filter state to sessionStorage
   */
  saveFilterState(state: Partial<TrainerFilterStateInput>): void {
    try {
      // Convert Date objects to ISO strings for storage
      const stateToSave: TrainerFilterState = {
        golfCourseSearch: state.golfCourseSearch,
        selectedGolfCourse: state.selectedGolfCourse,
        areaSearch: state.areaSearch,
        selectedArea: state.selectedArea,
        selectedAreaData: state.selectedAreaData,
        dateRangeStart: state.dateRangeStart,
        dateRangeEnd: state.dateRangeEnd,
        selectedDate: state.selectedDate,
        hasSearched: state.hasSearched,
        startDateModel: this.convertDateToString(state.startDateModel),
        endDateModel: this.convertDateToString(state.endDateModel)
      };
      
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      // Silently fail if storage is full or unavailable
      console.warn('Could not save filter state to sessionStorage:', error);
    }
  }

  /**
   * Convert Date object to ISO string, or return string/null as-is
   */
  private convertDateToString(date: Date | string | null | undefined): string | null | undefined {
    if (date === null || date === undefined) {
      return date;
    }
    if (date instanceof Date) {
      return date.toISOString();
    }
    return date; // Already a string
  }

  /**
   * Get saved filter state from sessionStorage
   */
  getFilterState(): TrainerFilterState | null {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;
      
      return JSON.parse(stored) as TrainerFilterState;
    } catch (error) {
      console.warn('Could not retrieve filter state from sessionStorage:', error);
      return null;
    }
  }

  /**
   * Clear saved filter state
   */
  clearFilterState(): void {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Could not clear filter state from sessionStorage:', error);
    }
  }

  /**
   * Check if filter state exists
   */
  hasFilterState(): boolean {
    try {
      return !!sessionStorage.getItem(this.STORAGE_KEY);
    } catch {
      return false;
    }
  }
}

